// Immutable edits to a resume draft. Every function returns a new draft; none of
// them touch the master profile, which the draft was copied from. Editing a
// copied fact marks it `edited` but never changes its provenance `state`: a fact
// that arrived as `needs-review` stays `needs-review` no matter how it is reworded
// in the draft. Only the profile's own confirm action can produce `confirmed`.

import type {
  AnyResumeEntry,
  ResumeCertificationEntry,
  ResumeDraft,
  ResumeEducationEntry,
  ResumeExperienceEntry,
  ResumeProjectEntry,
  ResumeSectionId,
  ResumeSkillEntry,
  TemplateId,
} from './types';
import { RESUME_SECTIONS } from './types';

function stamp(draft: ResumeDraft, now: string): ResumeDraft {
  return { ...draft, updatedAt: now };
}

const DEFAULT_NOW = (): string => new Date().toISOString();

export function setName(draft: ResumeDraft, name: string, now = DEFAULT_NOW()): ResumeDraft {
  return stamp({ ...draft, name }, now);
}

export function setTargetRole(draft: ResumeDraft, targetRole: string, now = DEFAULT_NOW()): ResumeDraft {
  return stamp({ ...draft, targetRole }, now);
}

export function setTemplate(draft: ResumeDraft, template: TemplateId, now = DEFAULT_NOW()): ResumeDraft {
  return stamp({ ...draft, template }, now);
}

export function setSummary(draft: ResumeDraft, value: string, now = DEFAULT_NOW()): ResumeDraft {
  return stamp({ ...draft, summary: { ...draft.summary, value } }, now);
}

export function setContactField(
  draft: ResumeDraft,
  field: 'fullName' | 'email' | 'phone' | 'location',
  value: string,
  now = DEFAULT_NOW(),
): ResumeDraft {
  return stamp({ ...draft, contact: { ...draft.contact, [field]: { ...draft.contact[field], value } } }, now);
}

/** Reads the entry list for a repeatable section without losing its type. */
function replaceEntryIn<K extends 'experience' | 'education' | 'skills' | 'certifications' | 'projects'>(
  draft: ResumeDraft,
  section: K,
  id: string,
  patch: Record<string, unknown>,
  now: string,
): ResumeDraft {
  const list = draft[section] as AnyResumeEntry[];
  const next = list.map((entry) =>
    entry.id === id ? ({ ...entry, ...patch, edited: true } as AnyResumeEntry) : entry,
  );
  return stamp({ ...draft, [section]: next } as ResumeDraft, now);
}

export function updateExperience(
  draft: ResumeDraft,
  id: string,
  patch: Partial<Omit<ResumeExperienceEntry, 'id' | 'state' | 'evidence' | 'edited'>>,
  now = DEFAULT_NOW(),
): ResumeDraft {
  return replaceEntryIn(draft, 'experience', id, patch, now);
}

export function updateEducation(
  draft: ResumeDraft,
  id: string,
  patch: Partial<Omit<ResumeEducationEntry, 'id' | 'state' | 'evidence' | 'edited'>>,
  now = DEFAULT_NOW(),
): ResumeDraft {
  return replaceEntryIn(draft, 'education', id, patch, now);
}

export function updateSkill(
  draft: ResumeDraft,
  id: string,
  patch: Partial<Omit<ResumeSkillEntry, 'id' | 'state' | 'evidence' | 'edited'>>,
  now = DEFAULT_NOW(),
): ResumeDraft {
  return replaceEntryIn(draft, 'skills', id, patch, now);
}

export function updateCertification(
  draft: ResumeDraft,
  id: string,
  patch: Partial<Omit<ResumeCertificationEntry, 'id' | 'state' | 'evidence' | 'edited'>>,
  now = DEFAULT_NOW(),
): ResumeDraft {
  return replaceEntryIn(draft, 'certifications', id, patch, now);
}

export function updateProject(
  draft: ResumeDraft,
  id: string,
  patch: Partial<Omit<ResumeProjectEntry, 'id' | 'state' | 'evidence' | 'edited'>>,
  now = DEFAULT_NOW(),
): ResumeDraft {
  return replaceEntryIn(draft, 'projects', id, patch, now);
}

export function removeEntry(
  draft: ResumeDraft,
  section: 'experience' | 'education' | 'skills' | 'certifications' | 'projects',
  id: string,
  now = DEFAULT_NOW(),
): ResumeDraft {
  const list = draft[section] as AnyResumeEntry[];
  return stamp(
    { ...draft, [section]: list.filter((entry) => entry.id !== id) } as ResumeDraft,
    now,
  );
}

export function moveEntry(
  draft: ResumeDraft,
  section: 'experience' | 'education' | 'skills' | 'certifications' | 'projects',
  id: string,
  delta: -1 | 1,
  now = DEFAULT_NOW(),
): ResumeDraft {
  const list = draft[section] as AnyResumeEntry[];
  const from = list.findIndex((entry) => entry.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= list.length) return draft;
  const next = [...list];
  const moved = next[from];
  const target = next[to];
  if (!moved || !target) return draft;
  next[from] = target;
  next[to] = moved;
  return stamp({ ...draft, [section]: next } as ResumeDraft, now);
}

/** Moves a section up or down in the rendered order. Edges are a no-op. */
export function moveSection(
  draft: ResumeDraft,
  section: ResumeSectionId,
  delta: -1 | 1,
  now = DEFAULT_NOW(),
): ResumeDraft {
  const order = draft.sectionOrder;
  const from = order.indexOf(section);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return draft;
  const next = [...order];
  const moved = next[from];
  const target = next[to];
  if (!moved || !target) return draft;
  next[from] = target;
  next[to] = moved;
  return stamp({ ...draft, sectionOrder: next }, now);
}

/**
 * Hides a section from the draft. The entries stay in the draft so the section
 * can be restored; nothing is removed from the master profile.
 */
export function removeSection(draft: ResumeDraft, section: ResumeSectionId, now = DEFAULT_NOW()): ResumeDraft {
  if (!draft.sectionOrder.includes(section)) return draft;
  return stamp({ ...draft, sectionOrder: draft.sectionOrder.filter((s) => s !== section) }, now);
}

/** Restores a hidden section at the end of the current order. */
export function restoreSection(draft: ResumeDraft, section: ResumeSectionId, now = DEFAULT_NOW()): ResumeDraft {
  if (draft.sectionOrder.includes(section)) return draft;
  const order = [...draft.sectionOrder, section];
  const canonical = RESUME_SECTIONS.filter((s) => order.includes(s));
  return stamp({ ...draft, sectionOrder: canonical }, now);
}

export function hiddenSections(draft: ResumeDraft): ResumeSectionId[] {
  return RESUME_SECTIONS.filter((section) => !draft.sectionOrder.includes(section));
}
